import type { FC } from 'react';
import { useMemo } from 'react';
import Button from '@components/Button';
import IconMdi from '@components/IconMdi';
import InputSelect from '@components/InputSelect';
import { Show } from '@components/Show';
import type { UIOnChangeFn } from '@components/types';
import { useSchemaStore } from '@ContractBuilder/store';
import type { BlockVisibilityCondition, BlockVisibilityValueType } from '@domain/schemas/block.schema';
import { useDeepCompareMemo } from '@hooks/use-deep-compare-memo';
import { mdiTrashCanOutline } from '@mdi/js';
import { isEnumOrCountrySubDivision } from '@root/helpers/schema/resolveSchemaTreeFromForm';
import type { SchemaTreeToFlatListResult } from '@root/helpers/schema/schemaTreeToFlatList';
import { isPPLFrontend } from '@root/src/utils/app-paths';
import clsx from 'clsx';
import { get } from 'lodash-es';

import { MATCH_OPTIONS, OPERATOR_OPTIONS, PRESENCE_STATUS_OPERATORS } from '../constants';
import { useBlockVisibilityForm } from '../controllers/context';
import type { VisibilityConfigPath } from '../types';

import { BlockVisibilityFieldSelector } from './BlockVisibilityFieldSelector';
import { ValueComponent } from './BlockVisibilityValue';
import { ComparatorTooltipText } from './ComparatorTooltipText';
import { RowPrefix } from './RowPrefix';

type SelectedFieldType = SchemaTreeToFlatListResult & { id: string; label: string; order: number };

interface BlockVisibilityRowProps {
  condition: BlockVisibilityCondition;
  idx: number;
  onChange: (value: BlockVisibilityValueType | undefined, name: VisibilityConfigPath) => void;
  onDelete: () => void;
}

export const BlockVisibilityRow: FC<BlockVisibilityRowProps> = ({ onChange, onDelete, condition, idx }) => {
  const { findField } = useSchemaStore(({ findField }) => ({ findField }));
  const { isDisabled, basePath, formValues } = useBlockVisibilityForm();
  const values = useDeepCompareMemo(() => get(formValues, basePath), [formValues, basePath]);
  const sharedProps = { isDisabled };
  const hideValue = ['IS EMPTY', 'IS NOT EMPTY'].includes(condition.operator);

  const parentAccessor = basePath + `.conditions.${idx}`;
  const fieldAccessor = parentAccessor + '.field';
  const matchAccessor = basePath + '.match';
  const operatorAccessor = parentAccessor + '.operator';

  const selectedFieldSource = useMemo(() => findField(condition.field), [condition.field, findField]);

  const selectedField: SelectedFieldType | null = selectedFieldSource
    ? {
        ...selectedFieldSource,
        id: selectedFieldSource.id,
        label: selectedFieldSource.title,
        order: selectedFieldSource.qid ?? Infinity,
      }
    : null;

  const prefix = values?.match ?? '-';
  const isFirst = idx === 0;
  const hasMatchSelector = idx === 1;
  const hasPrefix = !hasMatchSelector && !isFirst;

  const availableOperators = useMemo(() => {
    if (!isPPLFrontend) {
      return OPERATOR_OPTIONS;
    }
    const presenceStatusOnly = selectedField && isEnumOrCountrySubDivision(selectedField);
    return presenceStatusOnly ? PRESENCE_STATUS_OPERATORS : OPERATOR_OPTIONS;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedField?.id]);

  return (
    <div id={parentAccessor}>
      <div
        className={clsx(
          'grid w-full place-items-center gap-3',
          hideValue ? 'grid-cols-[80px_340px_165px_30px]' : 'grid-cols-[80px_340px_165px_245px_30px]',
        )}
      >
        {isFirst && <RowPrefix className="mt-8 justify-self-start">WHEN</RowPrefix>}
        {hasMatchSelector && (
          <InputSelect
            isSearchable={false}
            options={MATCH_OPTIONS}
            onChange={onChange as UIOnChangeFn}
            value={values?.match}
            id={matchAccessor}
            name={matchAccessor}
            className="w-fit justify-self-start"
            shouldRenderInsidePortal
            {...sharedProps}
            dataTestId="select:Match"
          />
        )}
        {hasPrefix && <RowPrefix className="justify-self-start">{prefix}</RowPrefix>}
        <BlockVisibilityFieldSelector
          label={isFirst ? 'Field' : undefined}
          onChange={onChange}
          value={selectedField}
          name={fieldAccessor}
          {...sharedProps}
          idx={idx}
        />

        <InputSelect
          labelText={isFirst ? 'Comparator' : undefined}
          helperText={isFirst ? <ComparatorTooltipText /> : undefined}
          dataTestId="select:Comparator"
          value={condition.operator}
          options={availableOperators}
          isSearchable={false}
          shouldRenderInsidePortal
          {...sharedProps}
          onChange={onChange as UIOnChangeFn}
          name={operatorAccessor}
          isDisabled={!selectedField}
        />

        <Show when={!hideValue}>
          <div className={clsx('w-full', 'min-w-0')}>
            <ValueComponent
              label={isFirst ? 'Value' : undefined}
              fieldOption={selectedFieldSource}
              isDisabled={isDisabled}
              onChange={onChange}
              value={condition.value}
              parentPath={parentAccessor}
            />
          </div>
        </Show>

        {isDisabled ? null : (
          <Button className={clsx('w-fit border-none p-2! shadow-none', isFirst && 'mt-7')} onClick={onDelete}>
            <IconMdi path={mdiTrashCanOutline} />
          </Button>
        )}
      </div>
    </div>
  );
};
